import { useEffect, useRef } from "react";
import type { PDFDocumentProxy } from "pdfjs-dist";
import { renderTextLayer } from "pdfjs-dist";

interface PdfTextLayerProps {
  pdf: PDFDocumentProxy;
  pageNumber: number;
  zoom: number;
  rotation: number;
}

export function PdfTextLayer({ pdf, pageNumber, zoom, rotation }: PdfTextLayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!pdf || !containerRef.current) return;
    let cancelled = false;

    async function render() {
      try {
        const page = await pdf!.getPage(pageNumber);
        const viewport = page.getViewport({ scale: zoom, rotation });
        const textContent = await page.getTextContent();
        if (cancelled || !containerRef.current) return;

        // Clear previous render
        containerRef.current.innerHTML = "";
        containerRef.current.style.width = `${viewport.width}px`;
        containerRef.current.style.height = `${viewport.height}px`;

        // Use pdfjs-dist's renderTextLayer for pixel-perfect positioning
        await renderTextLayer({
          textContentSource: textContent,
          container: containerRef.current,
          viewport,
          textDivs: [],
        }).promise;
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to render text layer:", err);
        }
      }
    }

    render();
    return () => {
      cancelled = true;
    };
  }, [pdf, pageNumber, zoom, rotation]);

  return <div ref={containerRef} className="pdf-text-layer absolute inset-0" />;
}
